import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    CellRendererProps,
    FlatList,
    LayoutChangeEvent,
    ListRenderItem,
    StyleSheet,
    useWindowDimensions,
} from 'react-native';
import Animated, { useSharedValue } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useNavigation, useScrollToTop } from '@react-navigation/native';

import { AppNavBar, NAV_BAR_HEIGHT } from '@/components/AppNavBar';
import { BottomFooterRG } from '@/components/BottomFooterRG';
import { SizedBox } from '@/components/SizedBox';
import { BOTTOM_TABS_HEIGHT } from '@/components/TabBar';
import { LEAGUE_NAVIGATOR_HEIGHT } from '@/components/league-navigator/utils/league-navigator-utils';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { LeagueFilters } from '@/feature/live-hub-sbk/components/LeagueFilters';
import {
    LAUNCHPAD_SCROLLED_BOTTOM_OFFSET,
    LOBBY_PICK_BAR_HEIGHT_WITH_MARGIN,
    ListEmptyComponent,
} from '@/feature/lobby/components/LobbyList';
import { StickyCellRendererComponent } from '@/feature/lobby/components/sticky-item/StickyCellRendererComponent';
import { StickyWrapper, useStickyScrollHandler } from '@/feature/lobby/hooks/use-sticky-translation';
import { LobbyProductSwitcherChipsHandle } from '@/feature/product-transition/components/LobbyProductSwitcherChipsHandle';
import {
    LOBBY_HANDLE_HEIGHT,
    LobbyWrapper,
    productSwitcherStyles,
    useLobbyListStyles,
} from '@/feature/product-transition/components/LobbyWrapper';
import { PRODUCT_CHIPS_HEIGHT } from '@/feature/product-transition/components/ProductSwitcherChips';
import { LobbyCarousel } from '@/feature/promohub/components/LobbyCarousel';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { WHATS_NEW_LOBBY_CARD_TOTAL_HEIGHT, WhatsNewLobbyCard } from '@/feature/whats-new/components/WhatsNewLobbyCard';
import { useWhatsNewStore } from '@/feature/whats-new/hooks/use-whats-new-store';
import { useWhatsNewVisibility } from '@/feature/whats-new/hooks/use-whats-new-visibility';
import { useIsSweepsProduct } from '@/hooks/use-is-sweeps-product';
import { usePrevious } from '@/hooks/use-previous';
import { useProductSwitchModal } from '@/hooks/use-product-switch';
import { SweepsLobbyTabNavigationProp } from '@/navigation/sweeps/types';
import { isWeb, showScrollIndicator } from '@/utils/constants-platform-specific';

import {
    competitionTileId,
    liveId,
    lobbyId,
    sportTileId,
    useSbkLobbySectionSelection,
} from '../../hooks/use-sbk-lobby-section-selection';
import { useSelectedNavigationEntry } from '../../hooks/use-selected-navigation-entry';
import { NavigationLaunchpad } from '../Launchpad/NavigationLaunchpad';
import { SubGroupChipsRow } from '../Launchpad/SubGroupChipsRow';
import { LeaguesList } from './LeaguesList';
import { LiveEventsSection } from './LiveEventsSection';
import { LobbyEventsSection } from './LobbyEventsSection';
import { SweepsHeader } from './SweepsHeader';

type LobbyLeagueSection =
    | 'handle'
    | 'predictions_header'
    | 'sweeps_header'
    | 'whatsNew'
    | 'launchpad'
    | 'promos'
    | 'leagues'
    | 'events'
    | 'live_event_list';

const PREDICTIONS_LOBBY_CONTENT: LobbyLeagueSection[] = [
    'handle',
    'predictions_header',
    'whatsNew',
    'launchpad',
    'promos',
    'leagues',
] as const;
const SWEEPS_LOBBY_CONTENT: LobbyLeagueSection[] = [
    'handle',
    'sweeps_header',
    'whatsNew',
    'launchpad',
    'promos',
    'leagues',
] as const;
const PREDICTIONS_LEAGUE_CONTENT: LobbyLeagueSection[] = [
    'handle',
    'predictions_header',
    'launchpad',
    'events',
] as const;
const SWEEPS_LEAGUE_CONTENT: LobbyLeagueSection[] = [
    'handle',
    'sweeps_header',
    'whatsNew',
    'launchpad',
    'events',
] as const;
const SWEEPS_LIVE_HUB_CONTENT: LobbyLeagueSection[] = ['handle', 'sweeps_header', 'launchpad', 'live_event_list'];
const PREDICTIONS_LIVE_HUB_CONTENT: LobbyLeagueSection[] = [
    'handle',
    'predictions_header',
    'launchpad',
    'live_event_list',
];

const getListToRender = (isSweepsProduct: boolean, isLobby: boolean, isLive: boolean) => {
    if (isSweepsProduct) {
        return isLobby ? SWEEPS_LOBBY_CONTENT : isLive ? SWEEPS_LIVE_HUB_CONTENT : SWEEPS_LEAGUE_CONTENT;
    }
    return isLobby ? PREDICTIONS_LOBBY_CONTENT : isLive ? PREDICTIONS_LIVE_HUB_CONTENT : PREDICTIONS_LEAGUE_CONTENT;
};

type SbkLobbyListProps = {
    leagueId?: string;
    sportId?: string;
    competitionId?: string;
};

export const SbkLobbyList = ({ leagueId, sportId, competitionId }: SbkLobbyListProps) => {
    const navigation = useNavigation<SweepsLobbyTabNavigationProp>();

    // Scroll to top when Home button is pressed
    const listRef = useRef<FlatList<LobbyLeagueSection>>(null);
    useScrollToTop(listRef);

    const isSweepsProduct = useIsSweepsProduct();
    const setSelectedTile = useSbkLobbySectionSelection(state => state.actions.setSelectedTile);
    const { selectedTile, hasEntry, selectedSubGroupId, selectedLeagueId } = useSelectedNavigationEntry();
    const isLobby = selectedTile === lobbyId;
    const isLive = selectedTile === liveId;
    const listToRender = getListToRender(isSweepsProduct, isLobby, isLive);

    const { onScroll, scrollDirection, scrollVelocity, scrollOffset } = useStickyScrollHandler();
    const { hasUnseen } = useWhatsNewVisibility();
    const setLobbyReady = useWhatsNewStore(state => state.actions.setLobbyReady);
    const { listMinHeight, leaguesOffset } = useElementsDimensions(hasUnseen);

    useEffect(() => {
        setLobbyReady(true);
    }, [setLobbyReady]);

    const insets = useSafeAreaInsets();
    const launchpad = useSharedValue(0);
    const handleStickPosition = useSharedValue(0);

    // Animate to top when the selected tile or sub-group changes
    const prevSelectedTile = usePrevious(selectedTile);
    const prevSubGroupId = usePrevious(selectedSubGroupId);
    useEffect(() => {
        const selectionChanged = prevSelectedTile !== selectedTile || prevSubGroupId !== selectedSubGroupId;
        if (scrollOffset.value > leaguesOffset && selectionChanged) {
            listRef?.current?.scrollToOffset({ animated: false, offset: leaguesOffset });
        }
    }, [leaguesOffset, listRef, prevSelectedTile, prevSubGroupId, scrollOffset, selectedSubGroupId, selectedTile]);

    // Reset selected tile to lobby when entering product
    useEffect(() => {
        if (leagueId) {
            setSelectedTile(leagueId);
        } else if (sportId) {
            setSelectedTile(sportTileId(sportId));
        } else if (competitionId) {
            setSelectedTile(competitionTileId(competitionId));
        } else {
            setSelectedTile(lobbyId);
            navigation.setParams({
                leagueId: lobbyId,
            });
        }
    }, [leagueId, sportId, competitionId, setSelectedTile, navigation]);

    const { lobbyHandleHeight } = useLobbyListStyles();

    const [selectedLiveLeagueId, setSelectedLiveLeagueId] = useState<string | null>(null);

    const renderListItem: ListRenderItem<string> = useCallback(
        ({ item }) => {
            switch (item) {
                case 'handle':
                    return (
                        <LobbyProductSwitcherChipsHandle
                            handleStickPosition={handleStickPosition}
                            scrollDirection={scrollDirection}
                            scrollOffset={scrollOffset}
                            scrollVelocity={scrollVelocity}
                        />
                    );
                case 'sweeps_header':
                    return <SweepsHeader />;
                case 'predictions_header':
                    return (
                        <MaxWidthWrapper>
                            <Box pb={'s6'}>
                                <AppNavBar />
                            </Box>
                        </MaxWidthWrapper>
                    );
                case 'launchpad':
                    return (
                        <StickyWrapper
                            initialPosition={launchpad}
                            scrollOffset={scrollOffset}
                            scrollDirection={scrollDirection}
                            scrollVelocity={scrollVelocity}
                        >
                            <Box backgroundColor={ds.colorToken.background.neutral.base}>
                                <NavigationLaunchpad />
                                <SubGroupChipsRow />
                            </Box>
                            {isLive ? (
                                <Box mb="s12">
                                    <LeagueFilters
                                        setSelectedLeagueId={setSelectedLiveLeagueId}
                                        selectedLeagueId={selectedLiveLeagueId}
                                    />
                                </Box>
                            ) : null}
                        </StickyWrapper>
                    );
                case 'whatsNew':
                    return <WhatsNewLobbyCard />;
                case 'promos':
                    return (
                        <MaxWidthWrapper>
                            <LobbyCarousel style={styles.lobbyCarousel} />
                        </MaxWidthWrapper>
                    );
                case 'leagues':
                    return (
                        <MaxWidthWrapper>
                            <Box mt="s8" minHeight={100}>
                                <LeaguesList />
                            </Box>
                            <BottomFooterRG marginHorizontal="s16" footerMarginTop="s64" />
                        </MaxWidthWrapper>
                    );
                case 'events':
                    return selectedLeagueId || hasEntry ? (
                        <MaxWidthWrapper>
                            <LobbyEventsSection leagueId={selectedLeagueId} />
                        </MaxWidthWrapper>
                    ) : null;
                case 'live_event_list':
                    return selectedLiveLeagueId ? (
                        <MaxWidthWrapper>
                            <LiveEventsSection leagueId={selectedLiveLeagueId} />
                        </MaxWidthWrapper>
                    ) : null;
                default:
                    return null;
            }
        },
        [
            handleStickPosition,
            scrollDirection,
            scrollOffset,
            scrollVelocity,
            launchpad,
            isLive,
            selectedLiveLeagueId,
            selectedLeagueId,
            hasEntry,
        ]
    );

    // using a custom cell renderer component to make sure
    // the sticky components are rendered above the other items
    const CellRendererComponent = useCallback(
        ({ children, style, ...props }: CellRendererProps<LobbyLeagueSection>) => {
            const isLaunchpad = props.item === 'launchpad';
            const isHandle = props.item === 'handle';

            const adjustedStyle = {
                style,
                // set custom zIndex for the sticky components
                // this is needed to make sure the sticky components are rendered above the other items
                zIndex: cellKeyToZIndex[props.item] ?? 0,
                top: 0,
            };

            return (
                <StickyCellRendererComponent
                    {...props}
                    shouldBeSticky={isLaunchpad}
                    style={adjustedStyle}
                    onLayout={(event: LayoutChangeEvent) => {
                        if (isLaunchpad) {
                            launchpad.value = event.nativeEvent.layout.y - insets.top;
                        }

                        if (isHandle) {
                            handleStickPosition.value =
                                event.nativeEvent.layout.y + PRODUCT_CHIPS_HEIGHT + lobbyHandleHeight;
                        }
                        props.onLayout?.(event);
                    }}
                >
                    {children}
                </StickyCellRendererComponent>
            );
        },
        [handleStickPosition, launchpad, insets.top, lobbyHandleHeight]
    );

    return (
        <LobbyWrapper>
            <AnimatedFlatList
                keyExtractor={keyExtractor}
                onScroll={onScroll}
                data={listToRender}
                contentContainerStyle={[
                    {
                        minHeight: listMinHeight + PRODUCT_CHIPS_HEIGHT + insets.top,
                    },
                    productSwitcherStyles.lobbyListContentContainerStyle,
                ]}
                windowSize={isWeb ? undefined : 5}
                // on web, the FlatList doesn't load the next page when scrolling
                initialNumToRender={isWeb ? listToRender.length : 20}
                maxToRenderPerBatch={10}
                ref={listRef}
                renderItem={renderListItem}
                CellRendererComponent={CellRendererComponent}
                ListFooterComponent={isLobby ? <SizedBox value={LOBBY_PICK_BAR_HEIGHT_WITH_MARGIN} /> : null}
                ListEmptyComponent={ListEmptyComponent}
                showsVerticalScrollIndicator={showScrollIndicator}
                // needed for sticky components to work on Android
                removeClippedSubviews={false}
            />
        </LobbyWrapper>
    );
};

/**
 * it returns the listMinHeight which is essential to avoid jumping of the list when it's length is recalculated
 * it returns the leaguesOffset which is the offset of the league cards list
 */
const useElementsDimensions = (hasUnseen: boolean) => {
    const logoHeaderHeight = NAV_BAR_HEIGHT;
    const insets = useSafeAreaInsets();
    const { switchingAvailable } = useProductSwitchModal();
    const { height } = useWindowDimensions();
    const lobbyListHeight = height - insets.top - insets.bottom - BOTTOM_TABS_HEIGHT;
    const whatsNewCardHeight = hasUnseen ? WHATS_NEW_LOBBY_CARD_TOTAL_HEIGHT : 0;
    const listMinHeight = lobbyListHeight + logoHeaderHeight + LEAGUE_NAVIGATOR_HEIGHT + whatsNewCardHeight;

    const leaguesOffsetSingleProduct = logoHeaderHeight + LAUNCHPAD_SCROLLED_BOTTOM_OFFSET + whatsNewCardHeight;
    const stickyLeaguesOffset =
        insets.top + logoHeaderHeight + LOBBY_HANDLE_HEIGHT + LAUNCHPAD_SCROLLED_BOTTOM_OFFSET + whatsNewCardHeight;

    return { listMinHeight, leaguesOffset: switchingAvailable ? stickyLeaguesOffset : leaguesOffsetSingleProduct };
};

const keyExtractor = (item: LobbyLeagueSection) => item;

const AnimatedFlatList = Animated.createAnimatedComponent(FlatList<LobbyLeagueSection>);

const cellKeyToZIndex: Partial<{ [key in LobbyLeagueSection]: number }> = {
    handle: 102,
    predictions_header: 101,
    sweeps_header: 101,
    launchpad: 101,
} as const;

const styles = StyleSheet.create({
    lobbyCarousel: {
        paddingTop: 8,
        paddingBottom: 24,
        borderBottomWidth: 1,
        borderBottomColor: ds.colorValue.border.strong,
    },
});
